import { X } from "@styled-icons/boxicons-regular";
import { Camera, Crown, File, Film, Music } from "@styled-icons/boxicons-solid";
import { observer } from "mobx-react-lite";
import { Channel, User } from "revolt.js";
import styles from "./Item.module.scss";
import classNames from "classnames";
import { useTriggerEvents } from "preact-context-menu";
import { Localizer, Text } from "preact-i18n";

import { IconButton } from "@revoltchat/ui";

import { getDisplayName } from "../../../lib/displayName";
import { isTouchscreenDevice } from "../../../lib/isTouchscreenDevice";
import {
    getAttachmentPreview,
    getSystemMessageBody,
    segmentEmojis,
} from "../../../lib/notificationBody";
import { prepareTextForNotification } from "../../../lib/prepareTextForNotification";

import { emojiDictionary } from "../../../assets/emojis";
import { getClientController } from "../../../controllers/client/ClientController";
import { modalController } from "../../../controllers/modals/ModalController";
import ChannelIcon from "../../common/ChannelIcon";
import { parseEmoji } from "../../common/Emoji";
import Tooltip from "../../common/Tooltip";
import UserIcon from "../../common/user/UserIcon";
import { Username } from "../../common/user/UserShort";
import UserStatus from "../../common/user/UserStatus";
import { RE_ULID } from "../../markdown/plugins/emoji";
import NotificationIcon from "./NotificationIcon";

type CommonProps = Omit<
    JSX.HTMLAttributes<HTMLDivElement>,
    "children" | "as"
> & {
    active?: boolean;
    alert?: "unread" | "mention";
    alertCount?: number;
    unreadCount?: number;
    margin?: boolean;
    muted?: boolean;
};

type UserProps = CommonProps & {
    user: User;
    context?: Channel;
    channel?: Channel;
};

// Chat-list preview of the last message (redmine #99371): text (with emoji
// segments), attachment icon + label, or the system message body. Used by
// both DM (UserButton) and group channels; `fallback` keeps the legacy
// behavior (user status / member count) for read channels.
const attachmentIcons = {
    photo: Camera,
    video: Film,
    audio: Music,
    file: File,
} as Record<string, (props: { size?: number }) => JSX.Element>;

function PreviewEmoji({ name }: { name: string }) {
    let url: string | undefined;

    // Same resolution as RenderEmoji (markdown/plugins/emoji.tsx):
    // ULID custom emoji → autumn URL, standard → the active emoji pack.
    if (RE_ULID.test(name)) {
        url = `${
            getClientController().availableClient.configuration?.features.autumn
                .url
        }/emojis/${name}`;
    } else if (name in emojiDictionary) {
        url = parseEmoji(emojiDictionary[name as keyof typeof emojiDictionary]);
    }

    // Unknown shortcode: skip (no raw ":name:" in the list).
    if (!url) return null;

    return (
        <img
            alt={`:${name}:`}
            loading="lazy"
            draggable={false}
            style={{ width: 16, height: 16, alignSelf: "center" }}
            src={url}
        />
    );
}

type MessagePreviewProps = {
    channel?: Channel;
    alert?: "unread" | "mention";
    fallback: JSX.Element;
};

const MessagePreview = observer((props: MessagePreviewProps) => {
    const { channel, alert, fallback } = props;

    if (!alert) return fallback;

    const message = channel?.last_message;
    if (!message) return fallback;

    const content = typeof message.content === "string" ? message.content : "";

    // Non-empty text: mentions/spoilers/markdown cleanup, 32-char cap,
    // then text spans + emoji images.
    if (content.length > 0) {
        const segments = segmentEmojis(
            prepareTextForNotification(message).slice(0, 32),
        );

        return (
            <>
                {segments.map((segment, index) =>
                    segment.type === "text" ? (
                        <span key={index}>{segment.value}</span>
                    ) : (
                        <PreviewEmoji key={index} name={segment.value} />
                    ),
                )}
            </>
        );
    }

    // No text: attachment icon + label (single) or count (multiple).
    const preview = getAttachmentPreview(message.attachments);
    if (preview) {
        const Icon = attachmentIcons[preview.icon];

        return (
            <>
                <Icon size={14} />
                {preview.kind === "single" ? (
                    preview.label
                ) : (
                    <Text
                        id="quantities.attachments"
                        plural={preview.count}
                        fields={{ count: preview.count }}
                    />
                )}
            </>
        );
    }

    // No text, no attachments: system message body (author display name,
    // falling back to the raw system id, BE parity).
    if (message.system) {
        const system = message.system;
        const id = "id" in system ? system.id : undefined;
        const user = id ? message.client.users.get(id) : undefined;
        const name = user ? getDisplayName(user) : id;

        return <>{getSystemMessageBody(system, name)}</>;
    }

    return fallback;
});

// TODO: Gray out blocked names.
export const UserButton = observer((props: UserProps) => {
    const {
        active,
        alert,
        margin,
        alertCount,
        unreadCount,
        user,
        context,
        channel,
        ...divProps
    } = props;

    return (
        <div
            {...divProps}
            className={classNames(styles.item, styles.user)}
            data-active={active}
            data-margin={margin}
            data-alert={typeof alert === "string"}
            data-online={
                typeof channel !== "undefined" ||
                (user.online && user.status?.presence !== "Invisible")
            }
            {...useTriggerEvents("Menu", {
                user: user._id,
                channel: channel?._id,
                unread: alert,
                contextualChannel: context?._id,
            })}>
            <UserIcon
                className={styles.avatar}
                target={user}
                size={32}
                status
                showServerIdentity
            />
            <div className={styles.name}>
                <div>
                    <Username user={user} showServerIdentity />
                </div>
                {
                    <div className={styles.subText}>
                        <NotificationIcon channel={channel} />
                        <MessagePreview
                            channel={channel}
                            alert={alert}
                            fallback={<UserStatus user={user} tooltip />}
                        />
                    </div>
                }
            </div>
            <div className={styles.button}>
                {context?.channel_type === "Group" &&
                    context.owner_id === user._id && (
                        <Localizer>
                            <Tooltip
                                content={<Text id="app.main.groups.owner" />}>
                                <Crown size={20} />
                            </Tooltip>
                        </Localizer>
                    )}
                {alert && (
                    // Draw red circle on DMs
                    <div className={styles.alert} data-style="mention">
                        {unreadCount}
                    </div>
                )}
            </div>
        </div>
    );
});

type ChannelProps = CommonProps & {
    channel: Channel;
    user?: User;
    compact?: boolean;
};

export const ChannelButton = observer((props: ChannelProps) => {
    const {
        active,
        alert,
        alertCount,
        unreadCount,
        channel,
        user,
        compact,
        muted,
        ...divProps
    } = props;

    if (channel.channel_type === "SavedMessages") throw "Invalid channel type.";
    if (channel.channel_type === "DirectMessage") {
        if (typeof user === "undefined") throw "No user provided.";
        return (
            <UserButton
                {...{ active, alert, alertCount, unreadCount, channel, user }}
            />
        );
    }

    const alerting = alert && !muted;
    return (
        <div
            {...divProps}
            data-active={active}
            data-alert={alerting}
            data-muted={muted}
            aria-label={channel.name}
            className={classNames(styles.item, {
                [styles.compact]: compact,
            })}
            {...useTriggerEvents("Menu", {
                channel: channel._id,
                unread: !!alert,
            })}>
            <div className={styles.avatar}>
                <ChannelIcon target={channel} size={compact ? 24 : 32} />
            </div>
            <div className={styles.name}>
                <div>{channel.name}</div>
                {channel.channel_type === "Group" && (
                    <div className={styles.subText}>
                        <NotificationIcon channel={channel} />
                        <MessagePreview
                            channel={channel}
                            alert={muted ? undefined : alert}
                            fallback={
                                <Text
                                    id="quantities.members"
                                    plural={channel.recipients!.length}
                                    fields={{
                                        count: channel.recipients!.length,
                                    }}
                                />
                            }
                        />
                    </div>
                )}
            </div>
            <div className={styles.button}>
                {alerting && (
                    <div className={styles.alert} data-style={alert}>
                        {alert === "mention" ? alertCount : unreadCount}
                    </div>
                )}
            </div>
        </div>
    );
});

type ButtonProps = CommonProps & {
    onClick?: () => void;
    children?: Children;
    className?: string;
    compact?: boolean;
};

export default function ButtonItem(props: ButtonProps) {
    const {
        active,
        alert,
        alertCount,
        onClick,
        className,
        children,
        compact,
        ...divProps
    } = props;

    return (
        <div
            {...divProps}
            className={classNames(
                styles.item,
                { [styles.compact]: compact, [styles.normal]: !compact },
                className,
            )}
            onClick={onClick}
            data-active={active}
            data-alert={typeof alert === "string"}>
            <div className={styles.content}>{children}</div>
            {alert && (
                <div className={styles.alert} data-style={alert}>
                    {alertCount}
                </div>
            )}
        </div>
    );
}
